import { type parameter_payloads as ddcParameterPayloads } from "ddc";
import { useCallback } from "react";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts/MediaDataRoomContext";
import { useMediaDataRoomInsightsData } from "features/mediaDataRoom/contexts/MediaDataRoomInsightsDataContext";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomJob,
} from "features/mediaDataRoom/hooks/ab";
import { MediaDataRoomVersion } from "features/mediaDataRoom/models";
import { type LookalikeAudienceStatisticsHookResult } from "./models";
import useLookalikeAudienceStatisticsState from "./useLookalikeAudienceStatisticsState";
import { transform } from "./utils";

interface LookalikeAudienceStatisticsHookPayload {
  audienceId: string | undefined;
  audienceReach: number;
  skip?: boolean;
  getParameterPayloads: (
    audienceId: string
  ) => ddcParameterPayloads.ParameterPayloads;
  excludeSeedAudience?: boolean;
}

export const useLookalikeAudienceStatistics = ({
  audienceId,
  audienceReach,
  getParameterPayloads,
  skip,
  excludeSeedAudience = false,
}: LookalikeAudienceStatisticsHookPayload): LookalikeAudienceStatisticsHookResult => {
  const { dataRoomId, driverAttestationHash } = useMediaDataRoom();
  const { session, publishedDatasetsHashes, getScopeId } =
    useMediaDataRoomInsightsData(MediaDataRoomVersion.ab);
  const computeJob = useMediaDataRoomJob({
    getScopeId,
    input: MediaDataRoomJobInput.create(
      "getLookalikeAudienceStatistics",
      dataRoomId,
      driverAttestationHash,
      publishedDatasetsHashes
    )
      // Lookalike audience statistics computation depends on everything except audiences dataset
      // TODO: Uncomment it when issue with jobs resolved
      // .withoutAudiencesDependency()
      .withResourceId(audienceId!),
    requestCreator: useCallback(
      (dataRoomIdHex, scopeIdHex) => ({
        dataRoomIdHex,
        generateAudience: getParameterPayloads(audienceId!).generate,
        scopeIdHex,
      }),
      [audienceId, getParameterPayloads]
    ),
    session,
    skip: skip || !audienceId,
    transform,
  });
  return useLookalikeAudienceStatisticsState({
    ...computeJob,
    audienceId,
    audienceReach,
    excludeSeedAudience,
  });
};
